import { Point } from './types.js';

/**
 *
 * @desc Finds the intersection point (if there is one) of the lines p1q1 and p2q2.
 * @param {Point} p1 The first point of the first line segment, which should always be an `[x, y]` formatted Array.
 * @param {Point} q1 The second point of the first line segment, which should always be an `[x, y]` formatted Array.
 * @param {Point} p2 The first point of the second line segment, which should always be an `[x, y]` formatted Array.
 * @param {Point} q2 The second point of the second line segment, which should always be an `[x, y]` formatted Array.
 * @returns {boolean} intersection point
 */
export const lineIntersection = (p1: Point, q1: Point, p2: Point, q2: Point): Point | null => {
  // allow for some margins due to numerical errors
  const eps: number = 1e-9;

  // find the intersection point between the two infinite lines
  const dx1: number = p1[0] - q1[0];
  const dx2: number = p2[0] - q2[0];
  const dy1: number = p1[1] - q1[1];
  const dy2: number = p2[1] - q2[1];

  const denom: number = dx1 * dy2 - dy1 * dx2;

  if (Math.abs(denom) < eps) return null;

  const cross1: number = p1[0] * q1[1] - p1[1] * q1[0];
  const cross2: number = p2[0] * q2[1] - p2[1] * q2[0];

  const px: number = (cross1 * dx2 - cross2 * dx1) / denom;
  const py: number = (cross1 * dy2 - cross2 * dy1) / denom;

  return [px, py];
};
